/**
 * Phase 10 R5 — useDayTechPayLines (BATCHED)
 *
 * Single-query day-scoped fetch of tech-pay columns for every ticket
 * scheduled for `dateStr` in the active shop. Mirrors the per-ticket shape
 * from `useTicketTechPayLines` but in one batched call.
 *
 * HARD CONTRACT: exactly **one** Supabase query per invocation.
 *
 * AUTHORITY (FF-A / FF-F):
 *   Reads from `public.v_ticket_jobs_tech_pay` (SECURITY INVOKER view) —
 *   see useTicketTechPayLines doc block for the masking contract. The
 *   view exposes `shop_id` and `scheduled_date` directly so the day
 *   filter is a flat `.eq(...)` pair, dropping the prior PostgREST
 *   inner-join embed on the parent tickets row.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import type { TicketTechPayLine } from '@/hooks/labor/useTicketTechPayLines';
import type { TechPayMode, TechPayStatus } from '@/lib/labor/computeJobTechPay';

export function useDayTechPayLines(dateStr: string | undefined, enabled = true) {
  const { activeShop } = useAuth();
  const shopId = activeShop?.id;

  return useQuery<TicketTechPayLine[]>({
    queryKey: ['day-tech-pay-lines', shopId, dateStr],
    enabled: !!shopId && !!dateStr && enabled,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('v_ticket_jobs_tech_pay')
        .select(
          `ticket_id, job_id,
           tech_pay_mode, tech_pay_minutes, tech_pay_rate_applied,
           tech_pay_amount, tech_pay_reason, tech_pay_status,
           job_name`,
        )
        .eq('shop_id', shopId)
        .eq('scheduled_date', dateStr)
        .is('deleted_at', null);
      if (error) throw error;

      return ((data ?? []) as any[]).map((r) => ({
        ticket_id: r.ticket_id,
        job_id: r.job_id,
        job_name: r.job_name ?? 'Unnamed job',
        tech_pay_mode: (r.tech_pay_mode as TechPayMode | null) ?? null,
        tech_pay_minutes:
          r.tech_pay_minutes != null ? Number(r.tech_pay_minutes) : null,
        tech_pay_rate_applied:
          r.tech_pay_rate_applied != null
            ? Number(r.tech_pay_rate_applied)
            : null,
        tech_pay_amount:
          r.tech_pay_amount != null ? Number(r.tech_pay_amount) : null,
        tech_pay_reason: r.tech_pay_reason ?? null,
        tech_pay_status: (r.tech_pay_status as TechPayStatus | null) ?? null,
        is_uncomputed: r.tech_pay_status == null,
      } satisfies TicketTechPayLine));
    },
  });
}
